"use client";

import React, { Fragment, useRef } from "react";
import { Popover, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "@/app/lib/hooks";
import useClickOutside from "@/hooks/useClickOutside";

/**
 * "Travaillez avec nous" dropdown matching the 2026 header design. Same
 * destinations as the legacy WorkWithUs.tsx (recruitment + travel-agents)
 * so existing links keep working.
 */
const WorkWithUsRedesign: React.FC = () => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const currentLang = pathname.split("/").filter(Boolean)[0] || "fr_FR";
  const { t } = useTranslation("guydeez");
  const cu = useAppSelector((s) => s.auth.user);

  const showBecomeGuide =
    !cu || (cu.role === 3 && cu.status === "guide_fill_form_in_progress");

  const handleClose = () => {
    const button = dropdownRef.current?.querySelector(
      'button[data-headlessui-state="open"]'
    ) as HTMLElement | null;
    button?.click();
  };
  useClickOutside(dropdownRef, handleClose);

  return (
    <div ref={dropdownRef} className="relative flex">
      <Popover className="self-center" style={{ display: "contents" }}>
        {({ open, close }) => (
          <>
            <Popover.Button
              className={`group inline-flex items-center text-sm font-medium text-white/90 hover:text-white focus:outline-none px-2 py-2 ${
                open ? "text-white" : ""
              }`}
            >
              <span>
                {t("nav_work_with_us", { defaultValue: "Travaillez avec nous" })}
              </span>
              <ChevronDownIcon
                className={`ml-1 h-4 w-4 transition-transform ${open ? "-rotate-180" : ""}`}
                aria-hidden="true"
              />
            </Popover.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute left-0 top-full z-40 mt-3 w-[320px]">
                <div className="rounded-2xl bg-white shadow-2xl ring-1 ring-black/5 overflow-hidden p-3">
                  {showBecomeGuide && (
                    <Link
                      href={`/${currentLang}/becoming-guide`}
                      onClick={() => close()}
                      className="flex items-start gap-3 px-3 py-3 rounded-lg hover:bg-neutral-50"
                    >
                      <span className="text-xl leading-none mt-0.5" aria-hidden>
                        🧭
                      </span>
                      <div>
                        <div className="text-sm font-semibold text-neutral-900">
                          {t("nav_become_guide_title", { defaultValue: "Guides" })}
                        </div>
                        <div className="text-xs text-neutral-500">
                          {t("nav_become_guide_desc", {
                            defaultValue:
                              "Devenez Guydeez et partagez votre ville",
                          })}
                        </div>
                      </div>
                    </Link>
                  )}
                  <Link
                    href={`/${currentLang}/travel-agents`}
                    onClick={() => close()}
                    className="flex items-start gap-3 px-3 py-3 rounded-lg hover:bg-neutral-50"
                  >
                    <span className="text-xl leading-none mt-0.5" aria-hidden>
                      🏢
                    </span>
                    <div>
                      <div className="text-sm font-semibold text-neutral-900">
                        {t("nav_become_partner_title", {
                          defaultValue: "Agences de voyages",
                        })}
                      </div>
                      <div className="text-xs text-neutral-500">
                        {t("nav_become_partner_desc", {
                          defaultValue: "Proposez nos tours à vos clients",
                        })}
                      </div>
                    </div>
                  </Link>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  );
};

export default WorkWithUsRedesign;
